import * as React from "react";
import { Tab, TabList, TabPanel, Tabs } from "react-tabs";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";

import DOMPurify from "dompurify";

import JobForm from "./job-form/JobFormContext";
import GeneralInfo, { AUTO_INVITE_POSITION_TYPES } from "./job-form/GeneralInfo";
import Skills from "./job-form/Skills";
import Compensation from "./job-form/Compensation";
import Description from "./job-form/Description";
import { payIsValid } from "./job-form/payValidity";
import { processValidationErrors } from "@/domains/profiles/utils/errorHandling";

type SubmitError =
  | { kind: "validation"; fieldErrors: Record<string, string | null>; messages: string[] }
  | { kind: "generic"; status?: number; statusText?: string };

// TypeScript interfaces
interface JobType {
  id: number;
  title: string;
  description_template?: string;
}

interface PositionType {
  shortname: string;
  name: string;
}

interface Practice {
  id: number;
  name: string;
  street_address?: string;
  city: string;
  state: string;
  zip?: string;
  avatar_url?: string;
  dide_writer_enabled?: boolean;
}

interface Skill {
  id: number;
  name: string;
  category: string;
  color: string;
}

interface JobSkill {
  id: number;
  category: string;
  name: string;
}

interface Job {
  id?: number;
  job_type_id?: number | string;
  confidential?: boolean;
  auto_invite_matches?: boolean;
  min_compensation?: string;
  compensation?: string;
  doe?: boolean;
  exact_pay?: boolean;
  compensation_type?: string;
  sunday_start?: string;
  monday_start?: string;
  tuesday_start?: string;
  wednesday_start?: string;
  thursday_start?: string;
  friday_start?: string;
  saturday_start?: string;
  sunday_end?: string;
  monday_end?: string;
  tuesday_end?: string;
  wednesday_end?: string;
  thursday_end?: string;
  friday_end?: string;
  saturday_end?: string;
  sunday_open?: boolean;
  monday_open?: boolean;
  tuesday_open?: boolean;
  wednesday_open?: boolean;
  thursday_open?: boolean;
  friday_open?: boolean;
  saturday_open?: boolean;
  years_experience?: string;
  job_title?: string;
  description?: string;
  practice_id?: number | string;
  temp_date?: string | Date;
  start_date?: string | Date;
  temp_start?: string;
  temp_end?: string;
  position_type?: string;
  benefit_medical?: boolean;
  benefit_dental?: boolean;
  benefit_vision?: boolean;
  benefit_401k?: boolean;
  benefit_pto?: boolean;
  benefit_bonuses?: boolean;
  sms_phone?: string;
}

interface FormProps {
  job: Job;
  practices: Practice[];
  skills: Skill[];
  job_skills: JobSkill[];
  job_types: JobType[];
  position_types: PositionType[];
  form_elements?: any;
  skill_categories: string[];
  authenticity_token: string;
  cancelButton?: boolean;
}

// Job Types are "Dental Assistant"....
// Position Types are "Full Time"....

const JobFormInitialState = (job: Job, job_skills: JobSkill[]) => {
  // Get temp_date from job and convert to date object
  if (job.temp_date) {
    job.temp_date = moment(job.temp_date).toDate();
  }
  // Convert start_date from string to Date object for DatePicker
  if (job.start_date) {
    job.start_date = moment(job.start_date).toDate();
  }

  if (job.compensation_type == undefined || job.compensation_type == null || job.compensation_type == "") {
    job.compensation_type = "hourly";
  }

  // Automatic invitations are on by default, but only fulltime and parttime
  // jobs are ever scored, so only those seed the key. A temp job must post no
  // value at all rather than posting one it cannot honour; GeneralInfo drops
  // the key again if the hiring type changes to one that is not scored.
  const autoInviteEligible = AUTO_INVITE_POSITION_TYPES.includes(job.position_type);
  if (autoInviteEligible && (job.auto_invite_matches == undefined || job.auto_invite_matches == null)) {
    job.auto_invite_matches = true;
  } else if (!autoInviteEligible) {
    delete job.auto_invite_matches;
  }

  return {
    ...job,
    skill_ids: job_skills.map(skill => skill.id),
  };
};

export default function Form({
  job,
  practices,
  skills,
  job_skills,
  job_types,
  position_types,
  form_elements,
  skill_categories,
  authenticity_token,
  cancelButton
}: FormProps) {
  const title = job.id ? "Edit Job" : "New Job";
  const [jobState, setJobState] = React.useState(
    JobFormInitialState(job, job_skills)
  );
  const [practicesState, setPracticesState] = React.useState(practices);

  const [tabIndex, setTabIndex] = React.useState(0);

  const [validation, setValidation] = React.useState(true);

  const [error, setError] = React.useState<SubmitError | null>(null);
  const [loading, setLoading] = React.useState(false);
  const previousTabIndex = React.useRef(tabIndex);

  // Pay is required on every posting. It is derived from jobState rather than
  // pushed up by the Compensation step: this effect below sets `validation`
  // false for any non-temp job once position/type/practice are filled in, and
  // it used to clobber the flag Compensation published, so the rail let a
  // poster jump to Description and submit with no pay. There is no flag to
  // clobber now - `submitBlocked` recomputes on every render.
  const payValid = payIsValid(jobState);
  const submitBlocked = validation || !payValid;

  const steps = [
    {
      label: "Job details",
      shortLabel: "Details",
      description: "Role, location, and hiring type",
      heading: "Start with the role",
      supportingText: "Tell candidates what you are hiring for and where they will work.",
      icon: "fa-briefcase",
    },
    {
      label: "Skills",
      shortLabel: "Skills",
      description: "Experience and qualifications",
      heading: "Define the right fit",
      supportingText: "Select the practical skills that matter most for this position.",
      icon: "fa-check-circle",
    },
    {
      label: "Compensation",
      shortLabel: "Pay",
      description: "Pay range and benefits",
      heading: "Make the offer clear",
      supportingText: "Transparent pay and benefits help qualified candidates act faster.",
      icon: "fa-dollar-sign",
    },
    {
      label: "Description",
      shortLabel: "Describe",
      description: "Headline and job story",
      heading: "Bring the opportunity to life",
      supportingText: "Finish with a useful headline and a description candidates can trust.",
      icon: "fa-align-left",
    },
  ];

  // Rail gating. Every step up to and including Compensation stays reachable:
  // pay being invalid is precisely the reason an owner needs to open the
  // Compensation step, and gating the whole strip on `submitBlocked` locked
  // them out of the fix (a legacy DOE draft could not be repaired at all).
  // Only the final step, which carries the submit, waits on pay.
  const stepIsLocked = (index: number) =>
    index > 0 && (index === steps.length - 1 ? submitBlocked : validation);

  const currentStep = steps[tabIndex] || steps[0];
  const selectedJobType = job_types.find(type => type.id.toString() === jobState.job_type_id?.toString());
  const selectedPositionType = position_types.find(type => type.shortname === jobState.position_type);

  React.useEffect(() => {
    if (previousTabIndex.current === tabIndex) return;

    previousTabIndex.current = tabIndex;
    const element = document.getElementById("jobformcontainer");
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [tabIndex]);

  React.useEffect(() => {
    const {
      position_type,
      job_type_id,
      practice_id,
      sms_phone,
      temp_date,
      temp_start,
      temp_end,
    } = jobState;
    if (
      position_type !== null &&
      position_type !== "" &&
      job_type_id !== null &&
      job_type_id !== "" &&
      practice_id !== null &&
      practice_id !== ""
    ) {
      if (jobState.position_type !== "temp") {
        setValidation(false);
      } else {
        //these are the checks for the temp position
        if (
          sms_phone !== null &&
          sms_phone !== "" &&
          temp_date !== null &&
          temp_date !== "" &&
          temp_start !== null &&
          temp_start !== "" &&
          temp_end !== null &&
          temp_end !== ""
        ) {
          setValidation(false);
        } else {
          setValidation(true);
        }
      }
    } else {
      setValidation(true);
    }
  }, [jobState]);

  const apiRequestData = {
    // request_profile: this.props.profile_id !== undefined,
    // profile_id: this.props.profile_id,
    job: jobState,
  };

  // Synchronous double-submit guard: two rapid clicks on "Create job" both run
  // before the `loading` re-render swaps the form for the spinner, and each
  // POST creates a job (verified: duplicate drafts in the PR sweep). React
  // state is async; a ref blocks the second call in the same tick.
  const submitInFlight = React.useRef(false);

  // The old "post without listed pay?" nudge is gone: pay is a required field
  // now (Compensation blocks Continue without it, and Job refuses to publish
  // without it), so a "Post anyway" escape hatch would only produce a listing
  // that cannot go live.

  // options.publish: "Publish job" saves AND publishes in one request (the
  // server answers with redirect_url: upsell page, job page, or a plan gate).
  // Without it, "Save draft" keeps the job unpublished and lands on the preview.
  function submitToApi(options: { publish?: boolean } = {}) {
    // Last line of defence: Description already refuses to call this while
    // submitBlocked, but a posting with no pay must never reach the API.
    if (!payIsValid(jobState)) {
      setTabIndex(2);
      return;
    }
    if (submitInFlight.current) return;
    submitInFlight.current = true;

    const url = apiRequestData.job.id
      ? `/jobs/${apiRequestData.job.id}.json`
      : "/jobs.json";
    const method = apiRequestData.job.id ? "PUT" : "POST";

    const headers = {
      "Content-Type": "application/json",
      "X-CSRF-Token": authenticity_token,
    };

    setLoading(true);
    setError(null);

    const handleNonOkResponse = async (response: Response) => {
      const body = await response.json().catch(() => ({}));
      if (response.status === 422) {
        const fieldErrors = processValidationErrors(body.errors || body);
        const messages = Object.entries(fieldErrors)
          .filter(([, msg]) => !!msg)
          .map(([field, msg]) => {
            const label = field.replace(/_/g, " ").replace(/^\w/, c => c.toUpperCase());
            return `${label} ${msg}`;
          });
        setError({ kind: "validation", fieldErrors, messages });
      } else {
        setError({ kind: "generic", status: response.status, statusText: response.statusText });
        if (typeof window.Sentry !== "undefined") {
          window.Sentry.captureException(
            new Error(`Form submit failed: ${response.status} ${response.statusText}`),
            {
              tags: { form: "Form", kind: "generic", status: String(response.status) },
              extra: {
                url,
                method,
                position_type: apiRequestData.job.position_type,
                job_id: apiRequestData.job.id,
              },
            }
          );
        }
      }
    };

    try {
      fetch(url, {
        method: method,
        headers: headers,
        body: JSON.stringify({ ...apiRequestData, publish: options.publish === true }),
      })
        .then(async response => {
          if (!response.ok) {
            console.error("API request failed:", response.status, response.statusText);
            await handleNonOkResponse(response);
            setLoading(false);
          submitInFlight.current = false;
            return null;
          }

          // Check if response is JSON before parsing
          const contentType = response.headers.get("content-type");
          if (contentType && contentType.includes("application/json")) {
            return response.json();
          } else {
            // Handle non-JSON responses (shouldn't happen with .json URLs)
            console.error("Expected JSON response but got:", contentType);
            setLoading(false);
          submitInFlight.current = false;
            setError({ kind: "generic" });
            return null;
          }
        })
        .then(data => {
          if (!data) return; // Error already handled

          // Extract job ID from response
          const jobId = data.id || (data.job && data.job.id) || apiRequestData.job.id;

          if (jobId) {
            const redirectUrl = typeof data.redirect_url === "string" && data.redirect_url.startsWith("/") ? data.redirect_url : `/jobs/${jobId}`;
            const allowedDomains = ["directdental.com", "test.directdental.com", "veracode.directdental.com", "localhost"];
            const url = new URL(redirectUrl, window.location.origin);
            if (allowedDomains.includes(url.hostname)) {
              window.location.href = DOMPurify.sanitize(url.href);
            } else {
              console.error("Redirect to untrusted domain blocked");
              setLoading(false);
          submitInFlight.current = false;
              setError({ kind: "generic" });
              if (typeof window.Sentry !== "undefined") {
                window.Sentry.captureException(
                  new Error(`Form untrusted redirect: ${url.hostname}`),
                  { tags: { form: "Form", kind: "untrusted_redirect" } }
                );
              }
            }
          } else {
            console.error("No job ID found in response:", data);
            setLoading(false);
          submitInFlight.current = false;
            setError({ kind: "generic" });
            if (typeof window.Sentry !== "undefined") {
              window.Sentry.captureException(
                new Error("Form submit: response missing job id"),
                { tags: { form: "Form", kind: "missing_job_id" } }
              );
            }
          }
        })
        .catch(err => {
          console.error("Form submission error:", err);
          setLoading(false);
          submitInFlight.current = false;
          setError({ kind: "generic" });
          if (typeof window.Sentry !== "undefined") {
            window.Sentry.captureException(err, {
              tags: { form: "Form", kind: "network" },
              extra: {
                url,
                method,
                position_type: apiRequestData.job.position_type,
                job_id: apiRequestData.job.id,
              },
            });
          }
        });
    } catch (err) {
      setLoading(false);
          submitInFlight.current = false;
      setError({ kind: "generic" });
      if (typeof window.Sentry !== "undefined") {
        window.Sentry.captureException(err, {
          tags: { form: "Form", kind: "unhandled" },
        });
      }
    }
  }

  // Hide form if error || loading == true
  // IF loading show a loading spinner
  // IF error show an error message

  return (
    <div className="job-builder-shell" data-testid="job-builder">
      {loading && (
        <div className="job-builder-state" aria-live="polite">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <h2>Creating your listing</h2>
          <p>Saving the details and preparing your job page.</p>
          <span className="visually-hidden">Processing</span>
        </div>
      )}
      {error && (
        <div className="job-builder-state job-builder-state--error" role="alert">
          <span className="job-builder-state__icon"><i className="fa fa-exclamation-triangle" aria-hidden="true"></i></span>
          <h2>
            {error.kind === "validation" ? "Please fix the following" : "Error"}
          </h2>
          {error.kind === "validation" && error.messages.length > 0 ? (
            <ul>
              {error.messages.map((msg, i) => (
                <li key={i}>{msg}</li>
              ))}
            </ul>
          ) : (
            <p>
              There was an error processing your request. Please try again
              {error.kind === "generic" && error.status ? ` (status: ${error.status})` : ""}.
            </p>
          )}
          <button
            type="button"
            className="job-builder-button job-builder-button--secondary"
            onClick={() => setError(null)}
          >
            Try Again
          </button>
        </div>
      )}
      {!loading && !error && (
        <Tabs
          className="job-builder"
          selectedIndex={tabIndex}
          onSelect={index => setTabIndex(index)}
        >
          <aside className="job-builder__rail">
            <div className="job-builder__rail-heading">
              <span>Job setup</span>
              <h2>{title}</h2>
              <p>Four focused steps, one strong listing.</p>
              <div className="job-builder__progress" aria-label={`Step ${tabIndex + 1} of ${steps.length}`}>
                <span style={{ width: `${((tabIndex + 1) / steps.length) * 100}%` }}></span>
              </div>
            </div>

            <TabList className="job-builder__steps" aria-label="Job creation steps">
              {steps.map((step, index) => (
                <Tab
                  key={step.label}
                  className="job-builder__step"
                  selectedClassName="job-builder__step--active"
                  disabledClassName="job-builder__step--disabled"
                  disabled={stepIsLocked(index)}
                >
                  <span className="job-builder__step-marker" aria-hidden="true">
                    {index < tabIndex ? <i className="fa fa-check"></i> : index + 1}
                  </span>
                  <span className="job-builder__step-copy">
                    <strong className="job-builder__step-label">{step.label}</strong>
                    <strong className="job-builder__step-short-label">{step.shortLabel}</strong>
                    <small>{step.description}</small>
                  </span>
                </Tab>
              ))}
            </TabList>
          </aside>

          <section className="job-builder__stage">
            <header className="job-builder__stage-heading">
              <span className="job-builder__stage-icon"><i className={`fa ${currentStep.icon}`} aria-hidden="true"></i></span>
              <div>
                <p>Step {tabIndex + 1} of {steps.length}</p>
                <h3>{currentStep.heading}</h3>
                <span>{currentStep.supportingText}</span>
              </div>
            </header>

            <JobForm.Provider value={[jobState, setJobState]}>
              <TabPanel className="job-builder__panel">
                <GeneralInfo
                  practices={practicesState}
                  job_types={job_types}
                  position_types={position_types}
                  tabIndexFunc={setTabIndex}
                  validation={validation}
                  setValidation={setValidation}
                  cancelButton={cancelButton}
                />
              </TabPanel>
              <TabPanel className="job-builder__panel">
                <Skills
                  skills={skills}
                  skill_categories={skill_categories}
                  tabIndexFunc={setTabIndex}
                />
              </TabPanel>
              <TabPanel className="job-builder__panel">
                <Compensation tabIndexFunc={setTabIndex} />
              </TabPanel>
              <TabPanel className="job-builder__panel">
                <Description
                  tabIndexFunc={setTabIndex}
                  validation={submitBlocked}
                  job_types={job_types}
                  practices={practicesState}
                  form_submit={submitToApi}
                />
              </TabPanel>
            </JobForm.Provider>
          </section>
        </Tabs>
      )}
    </div>
  );
}
